import { Component, Input, Output, EventEmitter, TemplateRef, ElementRef, ViewChild, OnChanges, SimpleChanges, HostListener } from '@angular/core';

export interface CarouselSlide<T = any> {
  data?: T;
  template?: TemplateRef<any>;
  imageUrl?: string;
  alt?: string;
  caption?: string;
  title?: string;     // names the slide in the list along the bottom, and to screen readers
  colour?: string;    // a strip of colour along the top while it shows, as the section it belongs to
}

type Direction = 'forward' | 'backward';

// slides shown one at a time over the page: a header with what they are and where you are, the slide, and the list of
// slides to jump to. Arrow keys, Home and End move between them and Escape closes it
@Component({
  selector: 'app-popup-carousel',
  templateUrl: './popup-carousel.html',
  styleUrls: ['./popup-carousel.scss'],
  standalone: false
})
export class PopupCarouselComponent implements OnChanges {
  @Input() open = false;
  @Input() slides: CarouselSlide[] = [];
  @Input() startIndex = 0;
  @Input() wrap = true;
  @Input() showDots = true;
  @Input() title = '';
  @Input() subtitle = '';

  @Output() openChange = new EventEmitter<boolean>();
  @Output() indexChange = new EventEmitter<number>();

  @ViewChild('dialog', { static: false }) set dialogRef(ref: ElementRef<HTMLElement> | undefined) {
    // focus it once it's shown, so the keys work straight away and screen readers start there
    if (ref && ref.nativeElement !== this.dialogElement) setTimeout(() => ref.nativeElement.focus());
    this.dialogElement = ref?.nativeElement;
  }
  private dialogElement?: HTMLElement;
  private returnFocus?: HTMLElement | null;

  index = 0;
  direction: Direction = 'forward';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] && this.open) {
      this.returnFocus = document.activeElement as HTMLElement | null;
      this.index = Math.max(0, Math.min(this.startIndex || 0, (this.slides?.length || 1) - 1));
      this.direction = 'forward';
    }
  }

  get currentSlide(): CarouselSlide | undefined {
    return this.slides?.[this.index];
  }

  get progress(): number {
    return this.slides?.length ? (100 * (this.index + 1)) / this.slides.length : 0;
  }

  get atStart() {
    return !this.wrap && this.index === 0;
  }

  get atEnd() {
    return !this.wrap && this.index === (this.slides?.length || 0) - 1;
  }

  next(): void {
    if (!this.slides?.length) return;
    const last = this.slides.length - 1;
    if (this.index === last) {
      if (this.wrap) this.show(0, 'forward');
    } else {
      this.show(this.index + 1, 'forward');
    }
  }

  prev(): void {
    if (!this.slides?.length) return;
    if (this.index === 0) {
      if (this.wrap) this.show(this.slides.length - 1, 'backward');
    } else {
      this.show(this.index - 1, 'backward');
    }
  }

  goTo(i: number): void {
    if (!this.slides?.length || i === this.index) return;
    this.show(i, i > this.index ? 'forward' : 'backward');
  }

  private show(i: number, direction: Direction): void {
    this.direction = direction;
    this.index = Math.max(0, Math.min(i, this.slides.length - 1));
    this.indexChange.emit(this.index);
    // the newly shown slide starts at its top
    this.dialogElement?.querySelector('.viewport')?.scrollTo({ top: 0 });
  }

  close(): void {
    this.open = false;
    this.openChange.emit(false);
    this.returnFocus?.focus?.();
  }

  backdropClick(e: MouseEvent): void {
    if ((e.target as HTMLElement).classList.contains('overlay')) this.close();
  }

  @HostListener('document:keydown', ['$event'])
  onKey(e: KeyboardEvent): void {
    if (!this.open) return;
    // leave typing in a field, and keys with modifiers, alone
    const target = e.target as HTMLElement;
    if (e.altKey || e.ctrlKey || e.metaKey || target?.closest?.('input, textarea, select, [contenteditable="true"]')) return;
    switch (e.key) {
      case 'Escape': this.close(); break;
      case 'ArrowRight': this.next(); break;
      case 'ArrowLeft': this.prev(); break;
      case 'Home': this.goTo(0); break;
      case 'End': this.goTo(this.slides.length - 1); break;
      default: return;
    }
    e.preventDefault();
  }
}
